import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { connect } from 'react-redux';
import { withRouter, Switch, Route, Redirect } from 'react-router-dom';
import moment from 'moment-timezone';

import PerDiemRequestForm from './components/PerDiemRequestForm';
import PerDiemRequestFormForDate from './components/PerDiemRequestFormForDate';

import ConfirmRequest from './components/ConfirmPerDiemRequest';
import PerDiemResults from './components/PerDiemResults';
import { requestPerDiem, requestPerDiemWithDate } from './actions';
import PageLayout from '../../layouts/PageLayout';
import { tryHandleGraphQLError } from '../../utils/graphQLErrorHandlers';

import { routerProps } from '../../propTypes';
import { getPerDiemFeature } from '../authentication/selectors';
import ErrorPage from './components/ErrorPage';

const propTypes = exact({
  perDiemFlag: PropTypes.bool.isRequired,
  requestPerDiemAction: PropTypes.func.isRequired,
  requestPerDiemWithDateAction: PropTypes.func.isRequired,
  ...routerProps,
});

const Page = (props) => {
  const formSubmit = (formValues) => {
    const { history, match } = props;
    history.push(`${match.path}/confirm`, { formValues });
  };

  const cancelSubmitToDateForm = (formValues) => {
    const { history, match } = props;
    history.push(`${match.path}/admin`, { formValues });
  };

  const cancelSubmitToForm = (formValues) => {
    const { history, match } = props;
    history.push(`${match.path}`, { formValues });
  };

  const doSubmit = async (values) => {
    const { history, match } = props;

    const {
      requestPerDiemAction,
    } = props;

    const employeeIds = values.employees.map((employee) => {
      return employee.id;
    });

    const perDiemRequestInputs = {
      ticket_number_id: values.ticket_number.id,
      user_ids: employeeIds,
    };

    try {
      const response = await requestPerDiemAction(perDiemRequestInputs);
      const results = response.result.data.per_diem_request;
      history.push(`${match.path}/results`, { results });
    } catch (e) {
      history.push(`${match.path}/error`, { errorMessage: tryHandleGraphQLError(e) });
    }
  };

  const doSubmitWithDate = async (values) => {
    const { history, match } = props;

    const {
      requestPerDiemWithDateAction,
    } = props;

    const employeeIds = values.employees.map((employee) => {
      return employee.id;
    });

    const requestedDate = new Date(values.date);
    const formattedDate = moment(requestedDate).format('YYYY-MM-DD');

    const perDiemRequestWithDateInputs = {
      date: formattedDate,
      ticket_number_id: values.ticket_number.id,
      user_ids: employeeIds,
    };

    try {
      const response = await requestPerDiemWithDateAction(perDiemRequestWithDateInputs);
      const results = response.result.data.per_diem_request_for_date;
      history.push(`${match.path}/results`, { results });
    } catch (e) {
      history.push(`${match.path}/error`, { errorMessage: tryHandleGraphQLError(e) });
    }
  };

  const requestAnotherDatePerDiem = () => {
    const { history, match } = props;
    history.push(`${match.path}`);
  };

  const { match, perDiemFlag } = props;
  return (
    <PageLayout
      title="Per Diem Requests"
      render={() => {
        return (
          <Switch>
            <Route
              path={`${match.path}/error`}
              render={({ location }) => {
                if (!(location.state && location.state.errorMessage)) {
                  return <Redirect to={match.path} />;
                }

                return <ErrorPage errorMessage={location.state.errorMessage} />;
              }}
            />
            <Route
              path={`${match.path}/results`}
              render={({ location }) => {
                if (!(location.state && location.state.results)) {
                  return <Redirect to={match.path} />;
                }

                return (
                  <PerDiemResults
                    results={location.state.results}
                    allowAdditionalRequests={perDiemFlag}
                    requestAnotherDatePerDiemAction={requestAnotherDatePerDiem}
                  />
                );
              }}
            />
            <Route
              path={`${match.path}/confirm`}
              render={({ location }) => {
                if (!(location.state && location.state.formValues)) {
                  return <Redirect to={match.path} />;
                }

                const submitFunction = (location.state && location.state.formValues.date)
                  ? doSubmitWithDate : doSubmit;

                /* NOTE: when canceling form submission,
                the page knows what form to show with initial values */
                const cancelSubmit = (perDiemFlag) ? cancelSubmitToDateForm : cancelSubmitToForm;

                return (
                  <ConfirmRequest
                    cancelSubmit={cancelSubmit}
                    formValues={location.state.formValues}
                    doSubmit={submitFunction}
                  />
                );
              }}
            />
            <Route
              path={`${match.path}/admin`}
              render={({ location }) => {
                if (!perDiemFlag) {
                  return <Redirect to={`${match.path}`} />;
                }

                const initialValues = (location.state && location.state.formValues)
                  ? location.state.formValues : {};

                return (
                  <PerDiemRequestFormForDate
                    formSubmit={formSubmit}
                    initialValues={initialValues}
                  />
                );
              }}
            />
            <Route
              path={match.path}
              render={({ location }) => {
                if (perDiemFlag) {
                  return <Redirect to={`${match.path}/admin`} />;
                }

                const initialValues = (location.state && location.state.formValues)
                  ? location.state.formValues : {};

                return (
                  <PerDiemRequestForm
                    formSubmit={formSubmit}
                    initialValues={initialValues}
                  />
                );
              }}
            />
          </Switch>
        );
      }}
    />
  );
};


Page.propTypes = propTypes;
const mapStateToProps = (state) => {
  return {
    perDiemFlag: getPerDiemFeature(state),
  };
};


const mapDispatchToProps = {
  requestPerDiemAction: requestPerDiem,
  requestPerDiemWithDateAction: requestPerDiemWithDate,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(withRouter(Page));
